import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import type { RunProgress } from "@mycompbox/shared";
import { Clock, Layers, CheckCircle2, Lightbulb, ChevronRight } from "lucide-react";
import {
  currentRunStage,
  formatEta,
  isUnsuccessfulRunState,
  runPercent,
  runStateLabel,
} from "@/lib/run-progress-format";

export interface SessionSummaryPaneProps {
  /**
   * The build's RunProgress (useRunProgress("build", epicId)), or null while
   * it loads or when the epic has no run. All counts, the current stage and
   * the elapsed time come from here; nothing is derived from events (ENG-370).
   */
  progress: RunProgress | null;
  decisionsCount?: number;
  onDecisionsClick?: () => void;
  className?: string;
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) return `${String(hrs)}h ${mins.toString().padStart(2, "0")}m`;
  return `${String(mins)}:${secs.toString().padStart(2, "0")}`;
}

function isRunning(progress: RunProgress | null): boolean {
  return progress?.state === "active" || progress?.state === "stalled";
}

/** Seconds since the run started; frozen at the last update once the run has ended. */
function useElapsedSeconds(progress: RunProgress | null): number | null {
  const startedAt = progress?.startedAt ?? null;
  const endedAt = progress && !isRunning(progress) ? progress.updatedAt : null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt || endedAt) return;
    setNow(Date.now());
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [startedAt, endedAt]);

  if (!startedAt) return null;
  const end = endedAt ? new Date(endedAt).getTime() : now;
  return Math.max(0, Math.floor((end - new Date(startedAt).getTime()) / 1000));
}

export function SessionSummaryPane({ progress, decisionsCount = 0, onDecisionsClick, className }: SessionSummaryPaneProps) {
  const elapsed = useElapsedSeconds(progress);

  const stateLabel = progress ? runStateLabel(progress.state) : null;
  const unsuccessful = progress ? isUnsuccessfulRunState(progress.state) : false;
  // A failed or cancelled run must not look part-way through: fill the bar and
  // colour it by outcome, matching the job page's ExecutionProgressPanel.
  const pct = progress ? (unsuccessful ? 100 : runPercent(progress)) : 0;
  const current = progress ? currentRunStage(progress) : null;
  const barColor =
    progress?.state === "failed"
      ? "bg-red-500/70"
      : progress?.state === "cancelled"
        ? "bg-zinc-500/70"
        : progress?.state === "stalled"
          ? "bg-amber-500/70"
          : "bg-gradient-to-r from-emerald-500 to-emerald-400";

  const stats = [
    {
      icon: Layers,
      label: "Now",
      value: current ? (current.detail ? `${current.label} · ${current.detail}` : current.label) : (stateLabel ?? "—"),
      color: "text-violet-400",
    },
    {
      icon: CheckCircle2,
      label: "Features",
      value: progress ? `${String(progress.completed)} / ${String(progress.total)}` : "—",
      color: "text-emerald-400",
    },
    {
      icon: Lightbulb,
      label: "Decisions",
      value: String(decisionsCount),
      color: "text-orange-400",
      onClick: onDecisionsClick,
      clickHint: Boolean(onDecisionsClick),
    },
    {
      icon: Clock,
      label: "Elapsed",
      value: elapsed === null ? "—" : formatDuration(elapsed),
      color: "text-amber-400",
    },
  ];

  return (
    <div className={cn("w-full", className)} data-testid="session-summary-pane">
      {/* Progress bar — thin, full width */}
      <div
        className="w-full h-1 bg-white/[0.04] rounded-full overflow-hidden mb-4"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-valuetext={stateLabel ?? `${String(pct)}%`}
      >
        <div
          className={cn("h-full transition-all duration-700 ease-out", barColor)}
          style={{ width: `${String(pct)}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((s) => {
          const Wrapper = s.onClick ? "button" : "div";
          return (
            <Wrapper
              key={s.label}
              onClick={s.onClick}
              className={cn(
                "flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-left",
                s.onClick && "cursor-pointer hover:bg-white/[0.05] hover:border-white/[0.12] transition-colors"
              )}
            >
              <s.icon className={cn("h-4 w-4 shrink-0", s.color)} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground leading-none mb-0.5 uppercase tracking-wider">
                  {s.label}
                </p>
                <p className="text-sm font-medium tabular-nums truncate" data-testid={`session-stat-${s.label.toLowerCase()}`}>
                  {s.value}
                </p>
              </div>
              {s.clickHint && (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
              )}
            </Wrapper>
          );
        })}
      </div>

      {/* Percentage / state label */}
      <p className="text-xs text-muted-foreground mt-2.5 tabular-nums" data-testid="session-progress-label">
        {stateLabel ?? `${String(pct)}% complete`}
        {isRunning(progress) && <> · {formatEta(progress?.eta)}</>}
      </p>
      {unsuccessful && progress?.failureReason && (
        <p className="text-xs text-red-400 mt-1" data-testid="session-progress-failure">
          {progress.failureReason}
        </p>
      )}
    </div>
  );
}
